import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, XCircleIcon, HelpCircleIcon, ArrowRightIcon, RefreshCwIcon } from 'lucide-react';
import { QuizQuestion } from '../data/module1';
interface SectionQuizProps {
  questions: QuizQuestion[];
  onComplete: () => void;
}
export function SectionQuiz({
  questions,
  onComplete
}: SectionQuizProps) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const currentQuestion = questions[currentQuestionIndex];
  const isCorrect = selectedAnswer === currentQuestion.correctIndex;
  const handleSelectAnswer = (index: number) => {
    if (isAnswered) return;
    setSelectedAnswer(index);
    setIsAnswered(true);
  };
  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
    } else {
      setIsCompleted(true);
      onComplete();
    }
  };
  const handleRetry = () => {
    setSelectedAnswer(null);
    setIsAnswered(false);
  };
  if (isCompleted) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="bg-green-50 rounded-xl p-6 border border-green-200 text-center" data-id="element-246">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-247">
          <CheckCircle2Icon className="w-8 h-8 text-[#8DC63F]" data-id="element-248" />
        </div>
        <h3 className="text-xl font-bold text-green-900 mb-2" data-id="element-249">
          Quiz erfolgreich abgeschlossen!
        </h3>
        <p className="text-green-800" data-id="element-250">
          Sie haben das Wissen aus diesem Abschnitt erfolgreich angewendet.
        </p>
      </motion.div>;
  }
  return <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-251">
      {/* Header */}
      <div className="bg-blue-50/50 border-b border-gray-100 p-4 flex items-center justify-between" data-id="element-252">
        <div className="flex items-center space-x-2" data-id="element-253">
          <div className="bg-blue-100 p-1.5 rounded-lg" data-id="element-254">
            <HelpCircleIcon className="w-4 h-4 text-blue-600" data-id="element-255" />
          </div>
          <h3 className="font-bold text-[#1a1a2e]" data-id="element-256">Wissens-Check</h3>
        </div>
        <div className="text-sm font-medium text-gray-500" data-id="element-257">
          Frage {currentQuestionIndex + 1} von {questions.length}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-1 w-full bg-gray-100" data-id="element-258">
        <div className="h-full bg-blue-500 transition-all duration-300" style={{
        width: `${currentQuestionIndex / questions.length * 100}%`
      }} data-id="element-259" />
      </div>

      {/* Question Content */}
      <div className="p-6" data-id="element-260">
        <h4 className="text-lg font-bold text-[#1a1a2e] mb-6" data-id="element-261">
          {currentQuestion.question}
        </h4>

        <div className="space-y-3" data-id="element-262">
          {currentQuestion.options.map((option, idx) => {
          let buttonClass = 'w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ';
          if (!isAnswered) {
            buttonClass += 'border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700';
          } else if (idx === currentQuestion.correctIndex) {
            buttonClass += 'border-[#8DC63F] bg-green-50 text-green-900';
          } else if (idx === selectedAnswer && !isCorrect) {
            buttonClass += 'border-red-400 bg-red-50 text-red-900';
          } else {
            buttonClass += 'border-gray-200 opacity-50 text-gray-500';
          }
          return <button key={idx} onClick={() => handleSelectAnswer(idx)} disabled={isAnswered} className={buttonClass} data-id="element-263">
                <div className="flex items-center justify-between" data-id="element-264">
                  <span className="font-medium" data-id="element-265">{option}</span>
                  {isAnswered && idx === currentQuestion.correctIndex && <CheckCircle2Icon className="w-5 h-5 text-[#8DC63F]" data-id="element-266" />}
                  {isAnswered && idx === selectedAnswer && !isCorrect && <XCircleIcon className="w-5 h-5 text-red-500" data-id="element-267" />}
                </div>
              </button>;
        })}
        </div>

        {/* Feedback & Actions */}
        <AnimatePresence data-id="element-268">
          {isAnswered && <motion.div initial={{
          opacity: 0,
          height: 0,
          marginTop: 0
        }} animate={{
          opacity: 1,
          height: 'auto',
          marginTop: 24
        }} className="overflow-hidden" data-id="element-269">
              <div className={`p-4 rounded-xl ${isCorrect ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-id="element-270">
                <div className="flex items-start space-x-3" data-id="element-271">
                  {isCorrect ? <CheckCircle2Icon className="w-5 h-5 text-[#8DC63F] mt-0.5 flex-shrink-0" data-id="element-272" /> : <XCircleIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" data-id="element-273" />}
                  <div data-id="element-274">
                    <h5 className={`font-bold mb-1 ${isCorrect ? 'text-green-900' : 'text-red-900'}`} data-id="element-275">
                      {isCorrect ? 'Richtig!' : 'Leider falsch.'}
                    </h5>
                    <p className={`text-sm ${isCorrect ? 'text-green-800' : 'text-red-800'}`} data-id="element-276">
                      {currentQuestion.explanation}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex justify-end" data-id="element-277">
                  {isCorrect ? <button onClick={handleNext} className="flex items-center px-4 py-2 bg-[#8DC63F] hover:bg-[#7ab036] text-white rounded-lg font-bold transition-colors" data-id="element-278">
                      {currentQuestionIndex < questions.length - 1 ? 'Nächste Frage' : 'Quiz abschließen'}
                      <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-279" />
                    </button> : <button onClick={handleRetry} className="flex items-center px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-bold transition-colors" data-id="element-280">
                      <RefreshCwIcon className="w-4 h-4 mr-2" data-id="element-281" />
                      Nochmal versuchen
                    </button>}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}